import React from 'react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';

const COLORS = {
    primary: '#00FF66',
    secondary: '#9B6FE8',
    accent: '#FFD700',
    danger: '#ef4444',
    muted: 'rgba(255,255,255,0.3)',
    background: 'rgba(255,255,255,0.05)'
};

// Custom tooltip style
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1a0a30] border border-white/10 rounded-lg p-3 shadow-lg">
                <p className="text-white/60 text-xs mb-1">{label}</p>
                {payload.map((entry, index) => (
                    <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
                        {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(4) : entry.value}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// Profit/Loss Line Chart over time
export function ProfitChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <div className="h-[200px] flex items-center justify-center text-white/30">
                No data available
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis 
                    dataKey="month" 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                />
                <YAxis 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                    tickFormatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                    type="monotone"
                    dataKey="profit"
                    stroke={COLORS.primary}
                    fill="url(#profitGradient)"
                    strokeWidth={2}
                    name="Profit (ETH)"
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}

// Win/Loss Bar Chart
export function WinLossChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <div className="h-[200px] flex items-center justify-center text-white/30">
                No data available
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis 
                    dataKey="month" 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                />
                <YAxis 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                    wrapperStyle={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}
                />
                <Bar dataKey="wins" fill={COLORS.primary} name="Wins" radius={[4, 4, 0, 0]} />
                <Bar dataKey="losses" fill={COLORS.danger} name="Losses" radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}

// Category Pie Chart
export function CategoryPieChart({ data }) {
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="h-[200px] flex items-center justify-center text-white/30">
                No data available
            </div>
        );
    }

    const chartData = Object.entries(data).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: value.count || 0,
        profit: value.profit || 0
    }));

    const PIE_COLORS = [COLORS.primary, COLORS.secondary, COLORS.accent, '#ff6b6b', '#4ecdc4'];

    return (
        <ResponsiveContainer width="100%" height={200}>
            <PieChart>
                <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                >
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                    wrapperStyle={{ fontSize: '10px' }}
                    formatter={(value) => <span className="text-white/70">{value}</span>}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}

// Odds Distribution Chart
export function OddsDistributionChart({ data }) {
    if (!data) {
        return (
            <div className="h-[150px] flex items-center justify-center text-white/30">
                No data available
            </div>
        );
    }

    const chartData = [
        { name: 'Low Risk (<1.5x)', value: data.low || 0, fill: COLORS.primary },
        { name: 'Medium (1.5-2.5x)', value: data.medium || 0, fill: COLORS.accent },
        { name: 'High Risk (>2.5x)', value: data.high || 0, fill: COLORS.danger }
    ];

    return (
        <ResponsiveContainer width="100%" height={150}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" stroke="rgba(255,255,255,0.3)" fontSize={10} />
                <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Bets" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

// Cumulative Profit Line Chart
export function CumulativeProfitChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <div className="h-[200px] flex items-center justify-center text-white/30">
                No data available
            </div>
        );
    }

    // Calculate cumulative profit
    let cumulative = 0;
    const chartData = data.map(item => {
        cumulative += item.profit || 0;
        return {
            ...item,
            cumulative: cumulative
        };
    });

    return (
        <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis 
                    dataKey="month" 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                />
                <YAxis 
                    stroke="rgba(255,255,255,0.3)" 
                    fontSize={10}
                    tickLine={false}
                    tickFormatter={(value) => `${value.toFixed(2)}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                    type="monotone"
                    dataKey="cumulative"
                    stroke={COLORS.accent}
                    strokeWidth={2}
                    dot={{ fill: COLORS.accent, strokeWidth: 0, r: 3 }}
                    name="Cumulative Profit (ETH)"
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

// Win Rate Gauge (simple visual)
export function WinRateGauge({ winRate }) {
    const normalizedRate = Math.min(100, Math.max(0, winRate || 0));
    const color = normalizedRate >= 50 ? COLORS.primary : COLORS.danger;
    
    return (
        <div className="relative w-full h-4 bg-white/10 rounded-full overflow-hidden">
            <div 
                className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                style={{ 
                    width: `${normalizedRate}%`,
                    backgroundColor: color
                }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                {normalizedRate.toFixed(1)}%
            </div>
        </div>
    );
}

// Chain P&L Breakdown Chart
export function ChainBreakdownChart({ data }) {
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="h-[200px] flex items-center justify-center text-white/30">
                No chain data available
            </div>
        );
    }

    const CHAIN_COLORS = { Base: '#0052FF', Arbitrum: '#28A0F0', Polygon: '#8247E5' };
    const chartData = Object.entries(data).map(([name, values]) => ({
        name,
        bets: values.bets || 0,
        profit: values.profit || 0,
        staked: values.staked || 0,
        wins: values.wins || 0,
        losses: values.losses || 0,
        fill: CHAIN_COLORS[name] || COLORS.secondary
    }));

    return (
        <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} />
                <YAxis stroke="rgba(255,255,255,0.3)" fontSize={10} tickLine={false} tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }} />
                <Bar dataKey="profit" name="P&L (ETH)" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.profit >= 0 ? entry.fill : COLORS.danger} />
                    ))}
                </Bar>
                <Bar dataKey="staked" name="Staked (ETH)" fill="rgba(255,255,255,0.15)" radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}

// Weekly Activity Heatmap
export function ActivityHeatmap({ data }) {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const maxVal = Math.max(...(data || [0]), 1);

    return (
        <div className="flex items-end gap-2 h-[120px]">
            {days.map((day, i) => {
                const value = data?.[i] || 0;
                const intensity = value / maxVal;
                const height = Math.max(8, intensity * 100);
                return (
                    <div key={day} className="flex-1 flex flex-col items-center gap-1">
                        <div className="text-xs font-mono text-white/40">{value}</div>
                        <div
                            className="w-full rounded-t-md transition-all duration-500"
                            style={{
                                height: `${height}%`,
                                backgroundColor: intensity > 0.7 ? COLORS.primary : intensity > 0.3 ? COLORS.accent : 'rgba(255,255,255,0.15)',
                                minHeight: '8px'
                            }}
                        />
                        <div className="text-[10px] text-white/30">{day}</div>
                    </div>
                );
            })}
        </div>
    );
}
